<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import { Table, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import { createFlatBaseColumns } from '@/utils/table'
  import { useIopFormStore } from '@/store/iop-form.store.ts'
  import type { ChangelogEntry, IopFormMessage } from '@/types/iop-form.js'
  import { isAxiosError } from 'axios'
  import IopChangelogModal from '@/components/idf-modal/IopChangelogModal.vue'

  const formStore = useIopFormStore()
  const showModal = ref(false)
  const selectedRow = ref<IopFormMessage>()
  const changelog = ref<ChangelogEntry[] | null>(null)
  const isLoading = ref(false)
  const toastStore = useToastStore()

  const groupedForms = computed(() => {
    const groups = new Map<string, IopFormMessage[]>()

    formStore.forms.forEach(form => {
      if (!groups.has(form.uuid)) {
        groups.set(form.uuid, [])
      }
      groups.get(form.uuid)!.push(form)
    })

    return Array.from(groups.values()).map(group => {
      const last = group.sort((formA: IopFormMessage, formB: IopFormMessage) => {
        const idA = formA.formVersion ?? Infinity
        const idB = formB.formVersion ?? Infinity
        return idA - idB
      })[group.length - 1]

      return { ...last, count: group.length }
    })
  })

  onMounted(async () => {
    await formStore.fetchAllIopForms()
    formStore.connectWS()
  })

  onBeforeUnmount(() => {
    formStore.disconnectWS()
  })

  async function onRowClick(row: IopFormMessage) {
    selectedRow.value = row
    isLoading.value = true
    let successfulFetch = true
    try {
      changelog.value = await formStore.fetchReadableChangelog(row.uuid)
    } catch (error) {
      successfulFetch = false
      if (isAxiosError(error)) {
        toastStore.error('Failed to fetch changelog')
        console.error('Failed to fetch changelog:', error.response?.data?.message)
      } else {
        throw error
      }
      changelog.value = null
    } finally {
      isLoading.value = false
    }
    showModal.value = successfulFetch
  }

  const iopFormMessageColumns = computed(() =>
    createFlatBaseColumns(
      groupedForms.value,
      [
        'form',
        'iopFormMessageOwner',
        'relations',
        'communicationDirection',
        'carrierEnterpriseNumbers',
        'ctbEnterpriseNumber',
        'treatmentFacilityEnterpriseNumber',
        'producerEnterpriseNumber',
      ],
      {
        columnOrder: ['id', 'uuid', 'createdDate', 'formVersion', 'status', 'count'],
        labelOverrides: {
          id: 'Formulier ID',
          uuid: 'UUID',
          createdDate: 'Aanmaak datum',
          sourceFormVersion: 'Formulier versie',
          formVersion: 'Huidige versie',
          count: 'Aantal formulieren',
          ritUUID: 'Rit UUID',
          stroomSignatureUUID: 'Stroom handtekening UUID',
        },
      }
    )
  )
</script>

<template>
  <IopChangelogModal
    v-model="showModal"
    :changelog="changelog"
    :selectedRow="selectedRow"
    :isLoading="isLoading"
  />
  <div class="layout">
    <Table
      :rows="groupedForms"
      :columns="iopFormMessageColumns"
      :configurable="true"
      configuration-storage-key="iop-form-overview-table"
      row-key="id"
      :search="true"
      :pagination="true"
      @rowClick="(e: IopFormMessage) => onRowClick(e)"
    >
      <template #cell-status="{ row }">
        <div :class="{ 'status-failed': row.status === 'FAILED' || row.status === 'CONFLICT' }">
          {{ row.status }}
        </div>
      </template>
    </Table>
  </div>
</template>

<style scoped>
  .status-failed {
    color: var(--colours-support-negative);
  }

  .layout {
    background: var(--colours-grey-1);
    display: flex;
    padding: var(--layout-layout-padding-top-app) var(--layout-layout-padding-top-app)
      var(--layout-layout-padding-bottom-app) var(--layout-layout-padding-bottom-app);
    flex-direction: column;
    gap: var(--layout-layout-spacing-app);
    flex: 1 0 0;
  }
</style>
